import React from 'react';
import axios from '@core/frontend/http';
import { FundingPoolCounter, HERO_VARIANT } from '@core/frontend/components';
import { AchievementsOverview, Project, ProjectList } from '@core/frontend/containers';
import { withHeroVisibility } from '@core/frontend/hoc';
import { useConfigStore, useFundingPoolBalance } from '@core/frontend/stores';
import { isPreviewContext } from '@core/frontend/shared';
import { determineVotes, findNextFundingStageIndex } from '@core/business-logic';
import { NumbersAndBrand } from '@core/ui';
import FundingVotePanel from './containers/Project/FundingVotePanel';
import ProjectMedia from './containers/Project/ProjectMedia';
import ProjectListItem from './containers/ProjectList/ProjectListItem/ProjectListItem';
import { achievementBadgeAssets } from './shared/badgeAssets';

/** Header-Zähler: der Fördertopf-Stand, live aus dem Config-Store. */
const FundingPoolCounterSlot = () => React.createElement(FundingPoolCounter, { balance: useFundingPoolBalance() });

/** Förderstufen der Kampagne; als Momentaufnahme, weil der Deskriptor kein Hook ist. */
const currentFundingStages = () => useConfigStore.getState().config?.funding ?? [];

/**
 * Teilen-Text der Detailseite.
 *
 * Der Text hängt an der nächsten Förderstufe und ist damit Produktlogik (Fördertopf,
 * Intended Difference #1). Gibt `undefined` zurück, wenn keine Förderstufen vorliegen; der
 * gemeinsame Teilen-Button fällt dann auf seinen Standardtext zurück.
 */
const buildFundingShareText = (voteCount, fundingStages) => {
    if (!fundingStages || voteCount === undefined || voteCount === null) return undefined;

    const nextStageIndex = findNextFundingStageIndex(voteCount, fundingStages);
    if (nextStageIndex === -1) {
        return 'Wir haben es geschafft und das Förderziel erreicht! Hier gibt es noch andere tolle Projekte denen wir helfen können ihr Förderziel zu erreichen';
    }

    const threshold = fundingStages[nextStageIndex]?.threshold ?? 0;
    const increment = fundingStages[nextStageIndex]?.increment ?? 0;
    return `Uns fehlen noch ${threshold - voteCount} Stimmen für eine Förderung von ${increment} Euro. Hilf uns das Förderungsziel zu erreichen und stimme jetzt für uns ab`;
};

/**
 * Projekt-ID der aktuell gerenderten Detailseite.
 *
 * `onShare` bekommt vom gemeinsamen Teilen-Button nur den Dienst, nicht das Projekt. Der
 * Teilen-Block existiert ausschliesslich auf `/projekte/:id`, deshalb liefert die aktuelle
 * Adresse die ID.
 */
const currentProjectId = () => {
    const match = /^\/projekte\/([^/]+)\/?$/.exec(window.location.pathname);
    return match ? match[1] : null;
};

/** Achievement-PATCH beim Teilen (Cassiopeia-spezifisch). */
const trackShareAchievement = (shareType) => {
    const projectId = currentProjectId();
    if (!projectId) return;

    axios.patch(`/achievements/${projectId}/${shareType}Shares`).catch((error) => {
        console.error('Error tracking share achievement:', error);
    });
};

/**
 * Produktdaten der Förder-Produktlinie. Der Vertrag steht in
 * `packages/frontend/src/routes/README.md` (Abschnitt 3); hier stehen nur Daten und
 * Funktionen, damit die geteilten Komponenten ohne Produktweiche auskommen.
 *
 * `votingModel` ist eine Produktkonstante dieser App: sie unterscheidet die
 * Produktlinien und ändert sich nicht je Kampagne. Deshalb steht sie hier und
 * nicht in `config/app.config.json`.
 */
export const product = {
    // Header-Zähler-Slot: der Fördertopf-Stand.
    counter: React.createElement(FundingPoolCounterSlot),

    // Voting-Modell dieser Produktlinie; entscheidet über die Finale-Route des Jury-Bereichs.
    votingModel: 'funding-pool',

    // Hero-Policy: kein Keyvisual, wenn die Seite es abgeschaltet hat (Projektliste und
    // Projektdetail registrieren das über ihre Container-Slots mit `useHeroVisibility`); sonst
    // wie die Wettbewerbs-Produktlinie — Standard-Keyvisual auf allen Seiten ausser der
    // Startseite, bzw. überall, wenn die Startseiten-Hero abgeschaltet ist.
    resolveHeroVariant: ({ isHomepage, hasHero, heroVisible }) =>
        heroVisible === false
            ? HERO_VARIANT.HIDDEN
            : !isHomepage || hasHero === false
              ? HERO_VARIANT.STANDARD
              : HERO_VARIANT.HERO,

    // Metrik-Platzierung: Vorgabe des Headers (kein Override).

    // Jury-Texte: diese Produktlinie spricht von Projekten und sortiert ohne Finale.
    juryCopy: {
        entityLabel: 'Projekte',
        emptyProjectListMessage: 'Keine Projekte gefunden.',
        searchPlaceholder: 'Suche nach Projekten oder Institutionen...',
        sortOptions: [
            { value: 'name', label: 'Teilnehmername' },
            { value: 'title', label: 'Projekttitel' },
            { value: 'voteCount', label: 'Stimmen' },
        ],
    },

    // Cookie-Präfix für Consent und Tracking-Gate der App-Shell.
    consentCookiePrefix: 'cassiopeia',

    // Wert für <BlockRendererConfigProvider value={...} />: kein Social-Share-Carousel, aber
    // die Vorgaben der Bestandsblöcke (volle Buttons, zentriert).
    blockRendererConfig: { socialShareDefaults: { displayMode: 'flat', alignment: 'center' } },

    // Darstellung der Projektliste: Raster mit Thumbnails (Intended Difference #2). Der Wert lebt
    // nur hier; der geteilte Container `ProjectList` liest ihn über `product.projectListView`.
    projectListView: 'grid',

    // Angezeigte Stimmenzahl der Projektliste: die geteilte Regel des Fördertopfs (erst nach
    // Abstimmungsstart öffentlich). Der Kern benutzt denselben Wert für die Screenreader-
    // Zusammenfassung.
    resolveVoteCount: ({ project, phases }) => determineVotes(project.voteCount, phases),

    // Abstimmbereich der Detailseite (Fördertopf: Förderstand, Stufen, SMS).
    projectVotePanel: FundingVotePanel,

    // Rasterkarte der Projektliste plus Banner über der Liste. Der Kern rendert Link und
    // Screenreader-Zusammenfassung.
    projectListComponents: { ProjectListItem, Banner: NumbersAndBrand },

    // Medienblock der Detailseite (Thumbnail + Abzeichen).
    projectDetailComponents: { Media: ProjectMedia },

    // Teilen-Dienste der Detailseite. Dieselbe Liste bietet auch die Wettbewerbs-Produktlinie an.
    projectShare: {
        services: [
            'whatsApp',
            'instagram',
            'facebook',
            'facebookMessenger',
            'pinterest',
            'telegram',
            'email',
            'bluesky',
            'linkedin',
            'xing',
        ],
        shareText: ({ project, phases }) =>
            buildFundingShareText(determineVotes(project.voteCount, phases), currentFundingStages()),
        onShare: trackShareAchievement,
    },

    // Standardüberschrift der ersten Teilnehmerantwort, wenn keine konfiguriert ist.
    contestantAnswerFallbackHeader: 'Was macht Ihre Karnevalsgruppe aus?',

    // Grafiken der Errungenschaften für `AchievementsOverview`.
    achievementBadges: achievementBadgeAssets,

    // Container-Slots der Projektseiten. Jede Seite registriert ihre Keyvisual-Sichtbarkeit über
    // den Hero-Hook; der Fördertopf blendet das Keyvisual auf Projektliste und Projektdetail aus
    // (Intended Difference #3).
    containers: {
        Project: withHeroVisibility(Project, 'ProjectDetail', false),
        ProjectList: withHeroVisibility(ProjectList, 'ProjectList', false),
    },

    // Produktspezifische Routen für <AppRoutes extraRoutes={...} />.
    extraRoutes: [{ path: '/errungenschaften', component: AchievementsOverview }],

    // Live-Preview-Gate der App-Shell: true = Tracking/Consent unterdrücken.
    previewGate: ({ location, isPreviewConnected }) => isPreviewContext(location) || isPreviewConnected,
};
